import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * useCollapsible — hook for sliced lists when the parent owns the rendering
 * (e.g. tables where the items must live inside <tbody>). Returns the slice
 * + a ready-to-render toggle button you can place wherever the design needs.
 *
 * Use this pattern in tables / cases where wrapping items in a <div> breaks
 * the layout. For pure <div> lists, prefer the `<CollapsibleList>` component.
 */
export function useCollapsible(items = [], initial = 5, testid) {
  const [expanded, setExpanded] = useState(false);
  const total = items.length;
  const overflow = Math.max(0, total - initial);
  const visible = expanded || total <= initial ? items : items.slice(0, initial);
  const Toggle = ({ className = "", colSpan }) => {
    if (overflow === 0) return null;
    const btn = (
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid={testid ? `${testid}-toggle` : "collapsible-toggle"}
        aria-expanded={expanded}
        className={`inline-flex items-center gap-1 text-xs font-semibold text-ek-purple transition-colors hover:text-ek-purple-deep ${className}`}
      >
        {expanded ? (
          <><ChevronUp size={13} /> Show fewer</>
        ) : (
          <><ChevronDown size={13} /> Show all {total} · {overflow} more</>
        )}
      </button>
    );
    if (typeof colSpan === "number") {
      return <tr><td colSpan={colSpan} className="px-2 py-2 text-center">{btn}</td></tr>;
    }
    return btn;
  };
  return { visible, overflow, expanded, total, Toggle };
}

/**
 * CollapsibleList — site-wide standard for long lists. Renders only the first
 * `initial` children and surfaces a "Show all N · X more" / "Show fewer" toggle
 * when the list exceeds the cutoff. Keeps long lists from dominating the page
 * while keeping every item one click away.
 *
 * Pass an array of already-rendered React nodes via `items`. Counts are derived
 * from `items.length` so callers don't have to plumb a separate `total`.
 */
export const CollapsibleList = ({
  items = [],
  initial = 5,
  showMoreLabel,
  showLessLabel = "Show fewer",
  testid,
  className = "",
  containerClassName = "",
}) => {
  const [expanded, setExpanded] = useState(false);
  const total = items.length;
  const overflow = Math.max(0, total - initial);
  const visible = expanded || total <= initial ? items : items.slice(0, initial);
  const moreLabel = showMoreLabel || `Show all ${total} · ${overflow} more`;

  return (
    <div className={containerClassName} data-testid={testid}>
      <div className={className}>{visible}</div>
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={testid ? `${testid}-toggle` : "collapsible-list-toggle"}
          aria-expanded={expanded}
          className="mt-2 flex items-center gap-1 text-xs font-semibold text-ek-purple transition-colors hover:text-ek-purple-deep"
        >
          {expanded ? (
            <>
              <ChevronUp size={13} /> {showLessLabel}
            </>
          ) : (
            <>
              <ChevronDown size={13} /> {moreLabel}
            </>
          )}
        </button>
      )}
    </div>
  );
};
